import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Select,
  Flex,
  Typography,
  Tag,
  Tooltip,
  Badge,
  Modal,
} from 'antd';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { TableRowActionItem } from '~/app/components/common/TableRowActionsMenu/types';
import { Button } from '@evimo/components/common/Button';
import {
  IconPencil,
  IconTrash,
  IconCheck,
  IconX,
  IconLink,
} from '@tabler/icons-react';
import type {
  CrudTableProps,
  ParsedCsvRow,
} from '../../components/knowledgeBase/crudTable/CrudTable.types';
import { editableCellInputStyle } from '../../components/knowledgeBase/crudTable/CrudTable.styles';
import {
  parseStringList,
  serializeStringList,
} from '~/app/hooks/knowledgeBase/stringListUtils';

const { Text } = Typography;

export const useTableColumns = ({
  config,
  isEditing,
  editingRows,
  onEditRow,
  onSaveRow,
  onCancelEdit,
  onDeleteRow,
  associationCounts,
  associationLabel,
  onOpenTransfer,
  associationCountsSecondary,
  associationLabelSecondary,
  onOpenTransferSecondary,
  onOpenSynonymsEdit,
  t,
}: {
  config: CrudTableProps['config'];
  isEditing: (key: string) => boolean;
  editingRows: CrudTableProps['editingRows'];
  onEditRow: CrudTableProps['onEditRow'];
  onSaveRow: CrudTableProps['onSaveRow'];
  onCancelEdit: CrudTableProps['onCancelEdit'];
  onDeleteRow: CrudTableProps['onDeleteRow'];
  associationCounts?: CrudTableProps['associationCounts'];
  associationLabel?: CrudTableProps['associationLabel'];
  onOpenTransfer?: CrudTableProps['onOpenTransfer'];
  associationCountsSecondary?: CrudTableProps['associationCountsSecondary'];
  associationLabelSecondary?: CrudTableProps['associationLabelSecondary'];
  onOpenTransferSecondary?: CrudTableProps['onOpenTransferSecondary'];
  onOpenSynonymsEdit?: (
    rowKey: string,
    title: string,
    synonyms: string[],
  ) => void;
  t: ReturnType<typeof useTranslation>['t'];
}) => {
  return [
    ...config.columns.map((col) => ({
      title: (
        <Flex align="center" gap={4}>
          <Text strong style={{ fontSize: 13 }}>
            {col.title}
          </Text>
          {col.required && (
            <Tag
              color="red"
              style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
            >
              {t('crudTable.required')}
            </Tag>
          )}
        </Flex>
      ),
      dataIndex: col.key,
      key: col.key,
      ellipsis: col.fieldType !== 'stringArray',
      width: col.fieldType === 'stringArray' ? 220 : undefined,
      onHeaderCell:
        col.fieldType === 'stringArray'
          ? () => ({
              style: {
                width: 220,
                minWidth: 220,
                maxWidth: 220,
              },
            })
          : undefined,
      onCell:
        col.fieldType === 'stringArray'
          ? () => ({
              style: {
                width: 220,
                minWidth: 220,
                maxWidth: 220,
                overflow: 'hidden',
              },
            })
          : undefined,
      align:
        col.key === 'associatedComponentTypesCount' ||
        col.key === 'associatedComponentTypeCount' ||
        col.key === 'associatedComponentClassesCount' ||
        col.key === 'associatedComponentClassCount' ||
        col.key === 'associatedSubsystemsCount' ||
        col.key === 'associatedAssetTypesCount'
          ? ('center' as const)
          : undefined,
      render: (
        value: string,
        record: Record<string, unknown> & { _key: string },
      ) => {
        if (
          (col.key === 'associatedComponentTypesCount' ||
            col.key === 'associatedComponentTypeCount' ||
            col.key === 'associatedComponentClassesCount' ||
            col.key === 'associatedComponentClassCount' ||
            col.key === 'associatedSubsystemsCount' ||
            col.key === 'associatedAssetTypesCount') &&
          onOpenTransfer
        ) {
          const count = parseInt(value || '0', 10);
          const typeLabel = col.key.includes('ComponentType')
            ? 'Component Types'
            : col.key.includes('ComponentClass')
              ? 'Component Classes'
              : col.key.includes('Subsystem')
                ? 'Subsystems'
                : col.key.includes('AssetType')
                  ? 'Asset Types'
                  : 'Items';

          return (
            <Tooltip
              title={t('associations.clickToManage', {
                count,
                type: typeLabel,
              })}
            >
              <Badge
                count={count}
                showZero
                color={count > 0 ? '#1E6DF6' : '#d9d9d9'}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const mappingId = String(
                    record.assetTypeSubsystemId ||
                      record.assetTypeSubsystemComponentClassId ||
                      record.assetTypeSubsystemComponentTypeId ||
                      record._key,
                  );
                  const code = String(
                    record.code ||
                      `${record.assetTypeCode}-${record.subsystemCode}${
                        record.componentTypeName
                          ? `-${record.componentTypeName}`
                          : ''
                      }`,
                  );
                  const name = String(
                    record.name ||
                      `${record.assetTypeCode} / ${record.subsystemCode}${
                        record.componentTypeName
                          ? ` / ${record.componentTypeName}`
                          : ''
                      }`,
                  );

                  onOpenTransfer(mappingId, code, name);
                }}
              />
            </Tooltip>
          );
        }

        if (col.fieldType === 'stringArray') {
          const sourceValue = isEditing(record._key)
            ? (editingRows[record._key]?.[col.key] ?? value)
            : value;
          const tags = parseStringList(sourceValue);
          const label = String(record.name || record.code || record._key);
          const canEdit = !config.readonly && !!onOpenSynonymsEdit;
          const visibleTags = tags.slice(0, 2);
          const hiddenTags = tags.slice(2);

          return (
            <Flex
              gap={4}
              align="center"
              style={{
                cursor: canEdit ? 'pointer' : 'default',
                width: '100%',
                maxWidth: 204,
                overflow: 'hidden',
                flexWrap: 'nowrap',
              }}
              onClick={
                canEdit
                  ? () => onOpenSynonymsEdit(record._key, label, tags)
                  : undefined
              }
            >
              {tags.length === 0 ? (
                <Text type="secondary">{t('synonymsDialog.empty')}</Text>
              ) : (
                <>
                  {visibleTags.map((tag) => (
                    <Tag
                      key={tag}
                      style={{
                        marginInlineEnd: 0,
                        maxWidth: 72,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {tag}
                    </Tag>
                  ))}
                  {hiddenTags.length > 0 ? (
                    <Tooltip
                      title={
                        <Flex vertical gap={4}>
                          {hiddenTags.map((tag) => (
                            <span key={tag}>{tag}</span>
                          ))}
                        </Flex>
                      }
                    >
                      <Tag
                        style={{ marginInlineEnd: 0, flexShrink: 0 }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        +{hiddenTags.length}
                      </Tag>
                    </Tooltip>
                  ) : null}
                </>
              )}
              {canEdit ? (
                <Tooltip title={t('synonymsDialog.edit')}>
                  <Button
                    type="text"
                    size="small"
                    iconLeft={<IconPencil size={14} />}
                    style={{ flexShrink: 0, marginInlineStart: 'auto' }}
                    onClick={(event: MouseEvent) => {
                      event.stopPropagation();
                      onOpenSynonymsEdit(record._key, label, tags);
                    }}
                  />
                </Tooltip>
              ) : null}
            </Flex>
          );
        }

        if (isEditing(record._key)) {
          // Count / derived columns are display-only — never edit them inline.
          if (col.excludeFromCsv) {
            return value != null && value !== '' ? String(value) : '—';
          }

          const editValue = editingRows[record._key]?.[col.key] ?? value ?? '';
          const editValueStr = String(editValue ?? '');
          const hasRegexError =
            col.regex &&
            editValueStr.trim() !== '' &&
            !new RegExp(col.regex).test(editValueStr.trim());

          if (col.options && col.options.length > 0) {
            return (
              <Select
                value={editValueStr || undefined}
                size="small"
                style={{ width: '100%' }}
                onChange={(val) => {
                  onEditRow(record._key, {
                    ...editingRows[record._key],
                    [col.key]: val,
                  });
                }}
                options={col.options.map((opt) => ({ label: opt, value: opt }))}
              />
            );
          }

          return (
            <Tooltip
              title={hasRegexError ? col.regexMessage : undefined}
              open={hasRegexError ? undefined : false}
              color="#e23b4a"
            >
              <Input
                value={editValueStr}
                size="small"
                status={hasRegexError ? 'error' : undefined}
                style={editableCellInputStyle}
                onChange={(e) => {
                  onEditRow(record._key, {
                    ...editingRows[record._key],
                    [col.key]: e.target.value,
                  });
                }}
              />
            </Tooltip>
          );
        }

        return (
          <Text style={{ fontSize: 13 }}>
            {value || <Text type="secondary">—</Text>}
          </Text>
        );
      },
    })),
    ...(associationCounts && associationLabel
      ? [
          {
            title: (
              <Flex align="center" gap={4}>
                <IconLink size={14} color="#1E6DF6" />
                <Text strong style={{ fontSize: 13 }}>
                  {associationLabel}
                </Text>
              </Flex>
            ),
            key: '_associations',
            width: 140,
            align: 'center' as const,
            render: (
              _: unknown,
              record: Record<string, unknown> & { _key: string },
            ) => {
              const code = record.code as string;
              const count = associationCounts[code] ?? 0;
              const isClickable = !!onOpenTransfer;
              return (
                <Tooltip
                  title={
                    isClickable
                      ? t('associations.clickToManage', {
                          count,
                          type: associationLabel,
                        })
                      : t('associations.countsOnly', { count })
                  }
                >
                  <Badge
                    count={count}
                    showZero
                    color={count > 0 ? '#1E6DF6' : '#d9d9d9'}
                    style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    onClick={
                      isClickable
                        ? () =>
                            onOpenTransfer(
                              record._key,
                              code,
                              String(record.name) || code,
                            )
                        : undefined
                    }
                  />
                </Tooltip>
              );
            },
          },
        ]
      : []),
    ...(associationCountsSecondary && associationLabelSecondary
      ? [
          {
            title: (
              <Flex align="center" gap={4}>
                <IconLink size={14} color="#1E6DF6" />
                <Text strong style={{ fontSize: 13 }}>
                  {associationLabelSecondary}
                </Text>
              </Flex>
            ),
            key: '_associations_secondary',
            width: 140,
            align: 'center' as const,
            render: (
              _: unknown,
              record: Record<string, unknown> & { _key: string },
            ) => {
              const code = record.code as string;
              const count = associationCountsSecondary[code] ?? 0;
              const isClickable = !!onOpenTransferSecondary;
              return (
                <Tooltip
                  title={
                    isClickable
                      ? t('associations.clickToManage', {
                          count,
                          type: associationLabelSecondary,
                        })
                      : t('associations.countsOnly', { count })
                  }
                >
                  <Badge
                    count={count}
                    showZero
                    color={count > 0 ? '#1E6DF6' : '#d9d9d9'}
                    style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    onClick={
                      isClickable
                        ? () =>
                            onOpenTransferSecondary(
                              record._key,
                              code,
                              String(record.name) || code,
                            )
                        : undefined
                    }
                  />
                </Tooltip>
              );
            },
          },
        ]
      : []),
    ...(!config.readonly
      ? [
          {
            title: (
              <Text strong style={{ fontSize: 13 }}>
                {t('crudTable.actions')}
              </Text>
            ),
            key: '_actions',
            width: 80,
            align: 'center' as const,
            render: (
              _: unknown,
              record: Record<string, unknown> & { _key: string },
            ) => {
              if (isEditing(record._key)) {
                return (
                  <Flex gap={4} justify="center">
                    <Button
                      type="text"
                      size="small"
                      iconLeft={<IconCheck size={16} color="#00a87e" />}
                      onClick={() => onSaveRow(record._key)}
                    />
                    <Button
                      type="text"
                      size="small"
                      iconLeft={<IconX size={16} color="#8c8c8c" />}
                      onClick={() => onCancelEdit(record._key)}
                    />
                  </Flex>
                );
              }

              const menuItems: TableRowActionItem[] = [
                ...(config.name !== 'Asset Type Manufacturer Model'
                  ? [
                      {
                        key: 'edit',
                        icon: <IconPencil size={16} />,
                        label: t('crudTable.edit'),
                        onClick: () => {
                          const rowData: ParsedCsvRow = {};
                          config.columns.forEach((col) => {
                            if (col.excludeFromCsv) return;
                            if (col.fieldType === 'stringArray') {
                              rowData[col.key] = serializeStringList(
                                record[col.key],
                              );
                              return;
                            }
                            rowData[col.key] = String(record[col.key] ?? '');
                          });
                          onEditRow(record._key, rowData);
                        },
                      },
                    ]
                  : []),
                {
                  key: 'delete',
                  danger: true,
                  icon: <IconTrash size={16} />,
                  label: t('crudTable.delete'),
                  onClick: () => {
                    Modal.confirm({
                      title: t('crudTable.deleteTitle'),
                      content: t('crudTable.deleteDescription'),
                      okText: t('crudTable.delete'),
                      cancelText: t('crudTable.cancel'),
                      okButtonProps: { danger: true },
                      onOk: () => onDeleteRow(record._key),
                    });
                  },
                },
              ];

              return <TableRowActionsMenu items={menuItems} />;
            },
          },
        ]
      : []),
  ];
};
